<script lang="ts">
import type { RegisteredComponent } from '@builder.io/sdk-svelte';
import { Content, isPreviewing } from '@builder.io/sdk-svelte';
import { CUSTOM_COMPONENTS } from '../../../CustomComponents.js';
import { BUILDER_PUBLIC_API_KEY } from '../../../config/apiKeys.js';
import { EHeroAspectRatios, ERoutes, ETitleFontSizes } from '../../../config/enums.js';
import { getServicePageSection } from '../../../lib/utils/servicePageSections.js';
import Breadcrumb from '../../../stories/molecules/Breadcrumb/Breadcrumb.svelte';
import Hero from '../../../stories/organisms/Hero/Hero.svelte';

const SERVICE_ROUTE_BASE = '/service';

interface BreadcrumbItem {
    text: string;
    to: string;
    active: boolean;
}

// biome-ignore lint/suspicious/noExplicitAny: Data type from +page.server.js
let { data }: { data: any } = $props();

let pageContent = $derived(data.builderProps.content?.data);
let canShowContent = $derived(data.builderProps.content || isPreviewing());
let servicePageNavigation = $derived(data.servicePageNavigation);
let servicesSection = $derived(getServicePageSection(pageContent?.servicesSection));
let breadcrumbItems: BreadcrumbItem[] = $derived([
    {
        text: 'Services',
        to: ERoutes.Services,
        active: false,
    },
    ...(servicesSection
        ? [
              {
                  text: servicesSection.label,
                  to: servicesSection.href,
                  active: false,
              },
          ]
        : []),
    {
        text: pageContent?.title || pageContent?.mastheadTitle || '',
        to: getServicePath(pageContent?.slug),
        active: true,
    },
]);

function getServicePath(slug?: string) {
    const normalizedSlug = slug?.replace(/^\/+|\/+$/g, '') || '';

    return normalizedSlug ? `${SERVICE_ROUTE_BASE}/${normalizedSlug}` : SERVICE_ROUTE_BASE;
}

function handleMobilePageNavChange(event: Event) {
    const target = event.currentTarget as HTMLSelectElement;
    const href = target.value;

    if (!href || href === window.location.pathname) return;

    window.location.href = href;
}
</script>

{#if canShowContent}
    {#key pageContent}
        {#if pageContent?.mastheadImage}
            <Hero
                image={{
                    src: pageContent?.mastheadImage,
                    alt: pageContent?.mastheadTitle || pageContent?.title || '',
                }}
                mobileImage={{
                    src: pageContent?.mastheadImageMobile || pageContent?.mastheadImage,
                    alt: pageContent?.mastheadTitle || pageContent?.title || '',
                }}
                content={pageContent?.mastheadTitle || pageContent?.title || ''}
                mastheadTextColour={pageContent?.mastheadTextColour || '#fff'}
                titleSize={ETitleFontSizes.H2}
                aspectRatio={EHeroAspectRatios.SHOWCASE}
            />
        {/if}

        <div class="service-page-shell">
            <div class="service-page-shell__layout">
                <div class="service-page-shell__breadcrumb">
                    <Breadcrumb items={breadcrumbItems} />
                </div>

                {#if servicePageNavigation?.items?.length}
                    <aside class="service-page-shell__nav" aria-label="Related service pages">
                        <p>
                            <a href={servicePageNavigation.href}>{servicePageNavigation.label}</a>
                        </p>
                        <ul>
                            {#each servicePageNavigation.items as item}
                                <li>
                                    <a href={item.href} class:active={item.active}>{item.label}</a>
                                </li>
                            {/each}
                        </ul>
                    </aside>

                    <div class="service-page-shell__mobile-nav">
                        <label for="service-page-nav">{servicePageNavigation.label}</label>
                        <select id="service-page-nav" onchange={handleMobilePageNavChange}>
                            {#each servicePageNavigation.items as item}
                                <option value={item.href} selected={item.active}>{item.label}</option>
                            {/each}
                        </select>
                    </div>
                {/if}

                <div class="service-page-shell__content">
                    <Content
                        model="service-page"
                        content={data.builderProps.content}
                        apiKey={BUILDER_PUBLIC_API_KEY}
                        customComponents={CUSTOM_COMPONENTS as RegisteredComponent[]}
                    />
                </div>
            </div>
        </div>
    {/key}
{:else}
    Content Not Found
{/if}

<style lang="scss">
    @use 'sass-mq/mq' as *;

    .service-page-shell {
        background: var(--colour-white);
        position: relative;
        z-index: 1;
    }

    .service-page-shell__layout {
        display: grid;
        column-gap: 4.2rem;
        grid-template-columns: 1fr;
        margin: 0;
        max-width: 132rem;
        padding: 0 var(--layout-pagegrid-margin) 8rem;
        row-gap: 1.6rem;

        @include mq($from: tablet) {
            column-gap: 4rem;
            grid-template-columns: minmax(18rem, 27rem) minmax(0, 65rem);
            justify-content: space-between;
            padding-bottom: 12rem;
            row-gap: 4rem;
        }

        @include mq($from: desktop) {
            max-width: calc(132rem + max(0px, (100vw - 132rem) / 2));
        }

        @include mq($from: wide) {
            column-gap: clamp(4rem, 17.57vw, 34rem);
            justify-content: start;
        }
    }

    .service-page-shell__breadcrumb {
        min-width: 0;

        @include mq($from: tablet) {
            grid-column: 2;
        }

        :global(.breadcrumb) {
            padding-left: 0;
            padding-right: 0;
        }
    }

    .service-page-shell__nav {
        display: none;

        @include mq($from: tablet) {
            align-self: start;
            display: block;
            grid-column: 1;
            position: sticky;
            grid-row: 2;
            top: 11rem;
        }
    }

    .service-page-shell__nav p,
    .service-page-shell__nav p a {
        color: var(--colour-grey);
        font-size: 1.6rem;
        line-height: 1.4;
        text-decoration: none;
    }

    .service-page-shell__nav p {
        margin: 0 0 1.6rem;
    }

    .service-page-shell__nav ul {
        display: flex;
        flex-direction: column;
        gap: 0.8rem;
        list-style: disc;
        margin: 0;
        padding-left: 1.8rem;
    }

    .service-page-shell__nav a {
        color: var(--colour-dreamless-sleep);
        font-size: 1.6rem;
        line-height: 1.4;
        text-decoration: none;
    }

    .service-page-shell__nav a:hover,
    .service-page-shell__nav a:focus-visible {
        text-decoration: underline;
    }

    .service-page-shell__nav a.active {
        font-weight: 600;
    }

    .service-page-shell__mobile-nav {
        display: grid;
        gap: 0.8rem;

        @include mq($from: tablet) {
            display: none;
        }
    }

    .service-page-shell__mobile-nav label {
        color: var(--colour-grey);
        font-size: 1.4rem;
        line-height: 1.2;
    }

    .service-page-shell__mobile-nav select {
        appearance: none;
        background: var(--colour-white);
        border: 1px solid var(--colour-noghrei-silver);
        border-radius: 0.2rem;
        color: var(--colour-grey);
        font: inherit;
        padding: 1.1rem 1.2rem;
        width: 100%;
    }

    .service-page-shell__content {
        min-width: 0;

        @include mq($from: tablet) {
            grid-column: 2;
            grid-row: 2;
        }
    }

    .service-page-shell__content > :global(div > .builder-blocks) {
        display: flex;
        flex-direction: column;
        gap: 4.2rem;
    }

    .service-page-shell__content :global(.faq.builder-text) {
        max-width: none;
        padding-left: 0;
        padding-right: 0;
    }

    .service-page-shell__content
        > :global(div > .builder-blocks > .builder-block[class*='builder-pixel-']) {
        display: none;
    }
</style>
